import { createAsync, useNavigate } from '@solidjs/router';
import {
	AccessCircleLogo,
	ActivityIndicator,
	Button,
	Carousel,
	CarouselItem,
	Container,
	Heading,
	Link,
	MessageBar,
	Page,
	Receipt,
	Section,
	Tag,
} from '@troon/ui';
import { createEffect, createSignal, For, Match, Show, Suspense, Switch, useContext } from 'solid-js';
import { cachedGet } from '@troon/api-client';
import { IconCircleCheck } from '@troon/icons/circle-check';
import dayjs from '@troon/dayjs';
import { IconUserAdd } from '@troon/icons/user-add';
import { IconArrowRightMd } from '@troon/icons/arrow-right-md';
import { IconCheck } from '@troon/icons/check';
import { twJoin } from '@troon/tailwind-preset/merge';
import { IconTag } from '@troon/icons/tag';
import { IconUserCircle } from '@troon/icons/user-circle';
import { IconUsers } from '@troon/icons/users';
import { IconInfo } from '@troon/icons/info';
import { IconShareAndroid } from '@troon/icons/share-android';
import { useUser } from '../../../../providers/user';
import { ReservationCtx } from '../../../../providers/reservation';
import { TeeTimeInfoHeader } from '../../../../components/reserve-tee-time/tee-time-info';
import { AccessInstructions } from '../../../../components/upsells/access-tee-time';
import { usePersisted } from '../../../../providers/persistence-store';
import { getAccessProducts } from '../../../../providers/card';
import AddOnPurchaseForm from '../../../../components/addons/addon-purchase-form';
import { createConfetti } from '../../../../components/confetti';
import { InvitePlayers } from './components/invite-players';
import type { Dayjs } from '@troon/dayjs';
import type { CarouselRef } from '@troon/ui';
import type { RouteDefinition, RouteSectionProps } from '@solidjs/router';

export default function ReservationConfirmation(props: RouteSectionProps) {
	const user = useUser();
	const account = createAsync(() => getAccount({}));
	const paymentInfo = createAsync(() => getPaymentInfo({ path: { id: props.params.reservationId! } }));
	const data = useContext(ReservationCtx)!;
	const [carousel, setRef] = createSignal<CarouselRef>();
	const [currentSlide, setCurrentSlide] = createSignal(0);
	const [checkRef, setCheckRef] = createSignal<HTMLElement>();
	const [addUGP, setAddUGP] = createSignal(false);
	const navigate = useNavigate();

	const [persisted] = usePersisted();
	const accessProducts = createAsync(
		async () => (await getAccessProducts({ query: { promoCode: persisted.promoCode?.value } }))?.products,
		{ deferStream: false },
	);
	const addons = createAsync(() => getAddons({}), { deferStream: false });

	createEffect(() => {
		carousel()?.on('select', (e) => {
			setCurrentSlide(e.selectedScrollSnap());
			window.scrollTo({ top: 0, behavior: 'smooth' });
		});
	});

	createConfetti({
		trigger: () => !!checkRef()?.getBoundingClientRect(),
		elementRef: checkRef,
	});

	return (
		<Container size="small">
			<Page class="relative gap-8 md:gap-12">
				<Suspense
					fallback={<ActivityIndicator class="size-16 text-brand">Confirming your Reservation</ActivityIndicator>}
				>
					<Heading as="h1" class="sr-only">
						Tee Time Confirmed!
					</Heading>
					<div role="tablist" aria-label="Post-booking steps" class="flex items-center justify-center">
						<For each={Array.from({ length: carousel()?.slideNodes().length ?? 0 }, (_, i) => i + 1)}>
							{(tab, i) => (
								<>
									<Show when={i() !== 0}>
										<div
											role="presentation"
											class={twJoin(
												'h-0.5 w-16 transition-colors',
												currentSlide() >= i() ? 'bg-brand-100' : 'bg-neutral',
											)}
										/>
									</Show>
									<div
										role="tab"
										aria-current={currentSlide() === i()}
										class={twJoin(
											'flex size-6 items-center justify-center rounded-full font-semibold transition-colors',
											currentSlide() >= i() ? 'bg-brand-100 text-brand-600' : 'bg-neutral text-neutral-700',
										)}
										onClick={() => {
											carousel()?.scrollTo(i());
										}}
									>
										<Show when={currentSlide() >= i()} fallback={<div class="size-2 rounded-full bg-current" />}>
											<IconCheck class="size-5" />
										</Show>
									</div>
								</>
							)}
						</For>
					</div>
					<Carousel
						description="Tee time completion step"
						ref={setRef}
						align="center"
						loop={false}
						outerClass="flex flex-col-reverse"
						withPagination={false}
					>
						<CarouselItem class="flex basis-full flex-col items-center justify-start gap-8 md:px-4">
							<Section layout="tight" class="items-center">
								<IconCircleCheck ref={setCheckRef} class="size-24 text-brand md:size-32" />
								<Heading as="h2" size="h1" class="text-center">
									Tee Time Confirmed!
								</Heading>
								<Show when={user()?.email}>
									<p class="text-center text-neutral-700">You will receive a confirmation email at {user()?.email}</p>
								</Show>
							</Section>
							<Section appearance="contained">
								<Show when={data.latest?.reservation}>
									{(res) => (
										<div class="border-b border-neutral pb-4">
											<TeeTimeInfoHeader
												cartIncluded={res().includesCart}
												courseName={data.latest?.reservation.course.name}
												date={dayjs.tz(res().teeTime.epoch, res().teeTime.tz) as Dayjs}
												holes={res().holeCount === 9 ? 'NINE' : 'EIGHTEEN'}
												players={res().playerCount}
												facilityName={res().facility.name}
												facilitySlug={res().facility.slug}
												facilityLogo={res().facility.logo}
												showInfo
											/>
										</div>
									)}
								</Show>
								<Show when={paymentInfo.latest?.receipt}>
									{(receipt) => (
										<Receipt
											items={receipt().items.map((i) => ({ label: i.label, amount: i.displayAmount }))}
											totals={receipt().totals.map((i) => ({ label: i.label, amount: i.amount.displayValue }))}
											totalDisclaimer="All amounts displayed are subject to additional taxes and fees, which may be added at check in."
											disclaimer={receipt().disclaimer || undefined}
											reimbursement={
												receipt().reimbursement
													? {
															label: 'Reimbursed at check-in',
															amount: receipt().reimbursement!.amount?.displayValue,
															disclaimer: receipt().reimbursement!.disclaimer,
														}
													: undefined
											}
										/>
									)}
								</Show>
								<Show
									when={
										user()?.accessRewardsOptIn &&
										paymentInfo.latest?.accessPointsEarned &&
										data.latest?.reservation.facility.supportsAccessPoints
									}
								>
									<MessageBar appearance="brand" size="md" class="text-sm">
										<MessageBar.Icon>
											<AccessCircleLogo color="brand" class="size-8" />
										</MessageBar.Icon>
										<MessageBar.Content>
											Earn up to <b class="font-bold">{paymentInfo.latest!.accessPointsEarned} Access Points</b> when
											you pay for your round.
										</MessageBar.Content>
									</MessageBar>
								</Show>
								<Show when={data()?.reservation.facility.supportsAccessPoints}>
									<div class="flex items-start gap-2">
										<IconInfo class="size-6 shrink-0 text-brand" />
										<p class="text-sm font-normal text-neutral-900">
											Invite other players to your tee time after booking so they earn Access Points for this round.
											Points will not be issued in-person at the shop.
										</p>
									</div>
								</Show>
							</Section>

							<Button
								appearance="primary"
								class="size-fit grow-0"
								onClick={() => {
									if ((carousel()?.slideNodes().length ?? 0) <= 1) {
										navigate(`/reservation/${props.params.reservationId}`);
										return;
									}
									carousel()?.scrollNext();
								}}
							>
								Continue <IconArrowRightMd />
							</Button>
						</CarouselItem>

						<CarouselItem class="flex basis-full flex-col items-center justify-start gap-8 md:px-4">
							<Section layout="tight" class="items-center">
								<IconShareAndroid class="size-24 text-brand md:size-32" />
								<Heading as="h2" size="h1" class="text-center">
									Invite Players
								</Heading>
								<p class="mx-8 text-center text-balance text-neutral-700">
									Make sure to invite players to your tee time so they can earn Access points. When they join, you can
									share Guest Passes, confirm everyone receives the right rate, and simplify the check-in process.
								</p>
							</Section>

							<Section layout="tight" class="w-full rounded-lg border border-neutral p-4 md:p-6">
								<Heading as="h2" size="h4" class="flex items-center gap-2">
									<IconUserAdd class="text-brand" />
									<span>Invite Players</span>
								</Heading>
								<InvitePlayers showRecentInvited expandRecent />
							</Section>

							<Button
								appearance="secondary"
								class="size-fit grow-0"
								onClick={() => {
									if ((carousel()?.slideNodes().length ?? 0) === 2) {
										navigate(`/reservation/${props.params.reservationId}`);
										return;
									}
									carousel()?.scrollNext();
								}}
							>
								Maybe Later <IconArrowRightMd />
							</Button>
						</CarouselItem>

						<Switch>
							<Match when={data()?.owner?.accessUpsell}>
								{(upsell) => (
									<CarouselItem class="flex basis-full flex-col items-center justify-start gap-8 md:px-4">
										<Section layout="tight" class="items-center">
											<IconTag class="size-24 text-brand md:size-32" />
											<Heading as="h2" size="h1" class="text-center">
												Save {upsell().savings.displayValue} on this Tee Time!
											</Heading>
											<Tag appearance="access">Limited-time Offer</Tag>
											<p class="mx-8 text-center text-balance text-neutral-700">
												Join Access Premium to save on this booking and future rounds.
											</p>
											<Show
												when={
													(account()?.savings.personal?.cents ?? 0) > 0 && account()?.savings.personal?.displayValue
												}
											>
												{(amount) => (
													<p class="text-neutral-600">
														<IconUserCircle /> You’ve already missed out on {amount()} in tee time savings!
													</p>
												)}
											</Show>
										</Section>

										<Section appearance="contained">
											<Heading as="h2" size="h4">
												How it Works
											</Heading>

											<Show when={accessProducts()?.find((p) => p.type === 'TROON_ACCESS_PLUS')}>
												{(access) => (
													<>
														<AccessInstructions
															subscriptionName={access().subscriptionName}
															productType={access().type}
															productCost={access().totalAmount.displayValue}
															disclosure={access().disclosure}
															savings={upsell().savings.displayValue}
														/>
														<Button as={Link} href={`/membership/checkout/${access().id}`}>
															Join Today & Save
														</Button>
													</>
												)}
											</Show>
										</Section>

										<Button
											appearance="secondary"
											class="size-fit grow-0"
											as={Link}
											href={`/reservation/${props.params.reservationId}`}
										>
											I don’t want to save <IconArrowRightMd />
										</Button>
									</CarouselItem>
								)}
							</Match>
							<Match
								when={
									account()?.access && !account()?.access?.productAddOns.includes('UNLIMITED_GUEST_PASSES') && addons()
								}
							>
								{(addons) => (
									<CarouselItem class="flex basis-full flex-col items-center justify-start gap-8 md:px-4">
										<Section layout="tight" class="items-center">
											<IconUsers class="size-24 text-brand md:size-32" />
											<Tag appearance="access">Access Add-on</Tag>
											<Heading as="h2" size="h1" class="text-center">
												Unlimited Guest Passes
											</Heading>
											<p class="mx-8 text-center text-balance text-neutral-700">
												If you’d like to bring unlimited guests at your Premium rate, you can add Unlimited Guest Passes
												to your membership for an additional $899/year.
											</p>
										</Section>
										<Show
											when={addUGP()}
											fallback={
												<Button onClick={() => setAddUGP(true)} class="mx-auto size-fit grow-0">
													Get Unlimited Guest Passes
												</Button>
											}
										>
											<Section appearance="contained">
												<AddOnPurchaseForm
													addOnProduct={addons().products[0]!}
													onPurchaseSuccess={() => {
														navigate(`/reservation/${props.params.reservationId}`);
													}}
												/>
											</Section>
										</Show>{' '}
										<Button
											appearance="secondary"
											class="size-fit grow-0"
											as={Link}
											href={`/reservation/${props.params.reservationId}`}
										>
											Maybe later <IconArrowRightMd />
										</Button>
									</CarouselItem>
								)}
							</Match>
						</Switch>
					</Carousel>
				</Suspense>
			</Page>
		</Container>
	);
}

const getPaymentInfo = cachedGet('/v1/reservations/{id}/payment-info');
const getAccount = cachedGet('/v0/users/account');
const getAddons = cachedGet('/v0/subscription-addons/products');

export const route = { info: { nav: { hero: false, appearance: 'minimal' } } } satisfies RouteDefinition;
